<template>
    <div class="student-theme wrapper flex flex-column justify-content-center align-items-center p-4  w-full h-full">
        <CustomBreadcrumb class="flex align-self-start"
            :items="[{ label: 'Profile Settings' }, { label: 'Profile' }]" />
        <span class="H6-Medium block text-center  mt-6">Your ILearn’d adventure starts here</span>
        <span class="Label block text-center mt-1">Add details about your self</span>
        <form class=" md:col-11 md:p-5 mt-4">
            <div class=" w-full col-12 grid ">

                <div class="col-12 md:col-6 md:pr-3 ">
                    <div class="flex flex-column gap-2 pt-2">
                        <label for="fullName" class="Lable">Full Name</label>
                        <InputText id="fullName" v-model="form.fullName" class="w-full" variant="filled" size="small" />
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2">
                        <label for="age" class="Lable ">Age</label>
                        <InputText id="age" v-model.number="form.age" type="number" class="w-full" variant="filled"
                            size="small" min="1" max="100" />
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2">
                        <label for="language" class="Lable">Language</label>
                        <Select v-model="form.language" appendTo="self" variant="filled" :options="languages" size="small"
                            optionLabel="value" placeholder="Select languages" class="w-full" />
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2">
                        <label for="grade" class="Lable">Grade</label>
                        <Select v-model="form.grade" appendTo="self" variant="filled" :options="gradeOptions" :loading="isLoadingGrades"
                            size="small" optionLabel="name" placeholder="Select grade or level" class="w-full" />
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2">
                        <label for="school" class="Lable">School</label>
                        <InputText id="school" v-model="form.school" class="w-full" variant="filled" size="small" />
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2">
                        <label for="city" class="Lable">City</label>
                        <Select v-model="form.city" appendTo="self" :options="cities" size="small" variant="filled" optionLabel="name"
                            placeholder="Select your city" class="w-full" />
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2">
                        <label for="dob" class="Lable">Date of Birth</label>
                        <DatePicker id="dob" v-model="form.dateOfBirth" appendTo="self" variant="filled" size="small" class="w-full" fluid
                            showIcon iconDisplay="input" dateFormat="yy-mm-dd" />
                    </div>
                </div>

                <div class="col-12 md:col-6 md:pr-3">
                    <div class="flex flex-column gap-2 pt-2">
                        <label for="nationality" class="Lable">Nationality</label>
                        <NationalitySelect id="nationality" v-model="form.nationality" label="" placeholder="Select your nationality" />
                    </div>
                </div>

                <div class="col-12  ">
                    <div class="flex flex-column gap-2 pt-2">
                        <label for="bio" class="block">Bio</label>
                        <Textarea id="bio" v-model="form.bio" rows="5" autoResize class="w-full" variant="filled" size="small" />
                    </div>
                </div>

                <div class="flex flex-column pt-4">
                    <Button label="Save" class="orange-button w-full md:w-auto" :disabled="!isFormDirty"
                        @click.prevent="handleSubmit" size="small" />
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, watch, defineEmits } from "vue";
import CustomBreadcrumb from '@/components/CustomBreadcrumb.vue';
import NationalitySelect from '@/components/common/NationalitySelect.vue';
import { Button, InputText, Select, Textarea, DatePicker } from "primevue";
import { languages } from "@/utils/options";
import { cities } from "@/utils/cities";
import { gradeOptions, isLoadingGrades } from "@/utils/options";

const emit = defineEmits(["update-prfile"]);

const props = defineProps({
    profile: {
        type: Object,
        required: true
    }
});

const form = ref({
    fullName: "",
    language: null,
    school: "",
    age: null,
    grade: null,
    city: null,
    bio: "",
    dateOfBirth: null,
    nationality: "Sri Lanka",
});

const originalForm = ref(null);

const calculateAge = (dobString) => {
    if (!dobString) return 'N/A';
    
    const today = new Date();
    const birthDate = new Date(dobString);
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    
    return age;
};

watch(
    () => props.profile,
    (newProfile) => {
        if (newProfile) {
            form.value = {
                fullName: newProfile.fullName || newProfile.username || "",
                language: validateLanguage(newProfile.preferredMedium) || null,
                school: newProfile.currentInstitution || "",
                age: newProfile.age || calculateAge(newProfile.dateOfBirth) || null,
                grade: validateGrade(newProfile.currentEducationLevel) || null,
                city: validateCity(newProfile.city) || null,
                bio: newProfile.bio || "",
                dateOfBirth: newProfile.dateOfBirth
                    ? new Date(newProfile.dateOfBirth)
                    : null,
                nationality: newProfile.nationality || "Sri Lanka",
            };
            originalForm.value = { 
                ...form.value,
                dateOfBirth: form.value.dateOfBirth ? new Date(form.value.dateOfBirth) : null
            };
        }
    },
    { immediate: true }
);

watch(
    gradeOptions,
    (newGrades) => {
        if (props.profile && newGrades?.length > 0 && !form.value.grade) {
            const grade = validateGrade(props.profile.currentEducationLevel);
            if (grade) {
                form.value.grade = grade;
                originalForm.value.grade = grade;
            }
        }
    }, { immediate: true }
);

// const isFormValid = computed(() => {
//     return (
//         form.value.fullName.trim() &&
//         form.value.age !== null &&
//         form.value.language &&
//         form.value.grade &&
//         form.value.school.trim() &&
//         form.value.city &&
//         form.value.bio.trim()
//     );
// });

const isFormDirty = computed(() => {
    if (!originalForm.value) return false;

    const currentForm = form.value;
    const initialForm = originalForm.value;

    const dateChanged = (currentForm.dateOfBirth?.getTime() || null) !== (initialForm.dateOfBirth?.getTime() || null);

    return (
        currentForm.fullName !== initialForm.fullName ||
        (currentForm.language?.key || null) !== (initialForm.language?.key || null) ||
        currentForm.school !== initialForm.school ||
        currentForm.age !== initialForm.age ||
        (currentForm.grade?.code || null) !== (initialForm.grade?.code || null) ||
        (currentForm.city?.code || null) !== (initialForm.city?.code || null) ||
        dateChanged ||
        currentForm.bio !== initialForm.bio ||
        currentForm.nationality !== initialForm.nationality
    );
});

function validateGrade(code) {
    if (!code || !gradeOptions.value || gradeOptions.value.length === 0) return null;
    return gradeOptions.value.find(g => g.code === code) || null;
}

function validateLanguage(val) {
    if (!val) return null;
    return languages.find(g => g.key === val || g.value === val || g.key.toLowerCase() === val.toLowerCase()) || null;
}

function validateCity(code) {
    return cities.find(g => g.code === code) || null;
}

function handleSubmit() {
    const payload = {
        fullName: form.value.fullName,
        age: form.value.age,
        preferredMedium: form.value.language?.key || null,
        city: form.value.city?.code || null,
        bio: form.value.bio,
        currentInstitution: form.value.school,
        currentEducationLevel: form.value.grade?.code || null,
        dateOfBirth: form.value.dateOfBirth
            ? form.value.dateOfBirth.toISOString()
            : null,
        nationality: form.value.nationality || null,
    }
    emit("update-prfile", payload);
}
</script>

<style scoped>
/* .wrapper {
    height: 95%;
    margin-bottom: 10px;
    background-color: white;
    border-radius: 10px;
    box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px,
        rgba(60, 64, 67, 0.15) 0px 1px 3px 1px;
} */
</style>
